/** Tracker integrations, issue creation, ticket routing and server API tokens. */
const en = {
  createIssue: {
    noTracker:
      'No issue tracker is connected anywhere in this organization yet. Configure GitHub, Jira, Linear or another tracker in Integrations first.',
    summary: 'Summary / Title',
    issueType: 'Issue Type',
    priority: 'Priority',
    assignee: 'Assignee',
    labels: 'Labels / Tags',
    autoAttach: 'Auto-Attach from Session',
    replayUrl: 'Session Replay URL',
    replayHint: 'Link to the session recording',
    aiSummary: 'AI Summary',
    aiSummaryHint: 'AI-generated behavior analysis',
    aiSummaryNone: 'No AI summary available',
    errorLogs: 'Error Logs',
    errorLogsHint: (n: number) => `${n} JS error(s) from session`,
    failed: 'Failed to create the issue. Check the tracker credentials in Integrations.',
    filed: 'Issue filed with session data',
    creates: (provider: string) => `Creates an issue in ${provider} with session data`,
    creating: 'Creating…',
    create: 'Create Issue',
  },
  routing: {
    title: 'Ticket routing',
    project: 'Project',
    goesTo: 'Tickets go to',
    askEachTime: 'Ask each time',
    saveFailed: 'Could not save the route.',
  },
  hub: {
    title: 'Integration Hub',
    project: 'Project',
    gateLead: 'Integrations are available on the Pro plan and above (APM on Scale). You’re on the',
    gateTail: 'plan.',
    viewPlans: 'View plans',
    profileName: 'Profile name',
    profileNewHelp:
      'Lets one provider be connected more than once: a repo per team, or two Jira sites.',
    profileLockedHelp: 'Profile names cannot be changed; remove and re-add to rename.',
  },
  tokens: {
    title: 'Server API tokens',
    bodyLead:
      'Programmatic access to the REST API from your backend, scripts or CI: list sessions, pull errors, open tickets. Org-wide and role-capped; send as',
    bodyTail: 'Endpoints are documented at',
    bodySecret:
      'Unlike a project’s SDK ingest key, which ships in a web page, a server token is a secret: it is shown once and stored only as a hash.',
    copied: 'Copied ✓',
    copy: 'Copy',
    tokenName: 'Token name',
    roleViewer: 'Read-only (VIEWER)',
    roleMember: 'Read + write (MEMBER)',
    roleAdmin: 'Admin',
    createFailed: 'Could not create the token.',
    columns: { name: 'Name', token: 'Token', role: 'Role', lastUsed: 'Last used', created: 'Created' },
    snippetFilled:
      'The token you just created is filled in above. It will not appear here after you dismiss it.',
    snippetEmpty:
      'The examples need a real token, and an existing one cannot be shown again. Only its hash is ' +
      'stored. Press “Fill with a new token” to mint one and drop it in. Full endpoint reference: ',
    fillWithToken: 'Fill with a new token',
    filling: 'Creating…',
  },
};

const tr: typeof en = {
  createIssue: {
    noTracker:
      'Bu organizasyonda henüz hiçbir yerde bağlı bir hata izleme sistemi yok. Önce Entegrasyonlar’dan GitHub, Jira, Linear ya da başka bir sistemi yapılandırın.',
    summary: 'Özet / Başlık',
    issueType: 'Kayıt türü',
    priority: 'Öncelik',
    assignee: 'Atanan',
    labels: 'Etiketler',
    autoAttach: 'Oturumdan otomatik ekle',
    replayUrl: 'Oturum kaydı bağlantısı',
    replayHint: 'Oturum kaydına bağlantı',
    aiSummary: 'AI özeti',
    aiSummaryHint: 'AI ile üretilmiş davranış analizi',
    aiSummaryNone: 'AI özeti yok',
    errorLogs: 'Hata kayıtları',
    errorLogsHint: (n: number) => `Oturumdan ${n} JS hatası`,
    failed:
      'Kayıt oluşturulamadı. Entegrasyonlar’daki sistem kimlik bilgilerini kontrol edin.',
    filed: 'Kayıt, oturum verisiyle birlikte açıldı',
    creates: (provider: string) => `${provider} üzerinde oturum verisiyle bir kayıt açar`,
    creating: 'Oluşturuluyor…',
    create: 'Kaydı oluştur',
  },
  routing: {
    title: 'Talep yönlendirme',
    project: 'Proje',
    goesTo: 'Talepler şuraya gider',
    askEachTime: 'Her seferinde sor',
    saveFailed: 'Yönlendirme kaydedilemedi.',
  },
  hub: {
    title: 'Entegrasyon Merkezi',
    project: 'Proje',
    gateLead:
      'Entegrasyonlar Pro ve üzeri planlarda kullanılabilir (APM Scale’de). Şu anda şu plandasınız:',
    gateTail: '',
    viewPlans: 'Planları gör',
    profileName: 'Profil adı',
    profileNewHelp:
      'Bir sağlayıcının birden fazla kez bağlanmasını sağlar: ekip başına bir depo ya da iki Jira sitesi.',
    profileLockedHelp: 'Profil adları değiştirilemez; yeniden adlandırmak için kaldırıp tekrar ekleyin.',
  },
  tokens: {
    title: 'Sunucu API belirteçleri',
    bodyLead:
      'Kendi sunucunuzdan, betiklerinizden ya da CI’dan REST API’ye programatik erişim: oturumları listeleyin, hataları çekin, talep açın. Organizasyon geneli ve rol tavanlı; şu şekilde gönderin:',
    bodyTail: 'Uç noktalar şurada belgelenmiştir:',
    bodySecret:
      'Bir web sayfasına gömülen proje SDK veri alım anahtarının aksine, sunucu belirteci bir sırdır: yalnızca bir kez gösterilir ve sadece karması saklanır.',
    copied: 'Kopyalandı ✓',
    copy: 'Kopyala',
    tokenName: 'Belirteç adı',
    roleViewer: 'Salt okunur (VIEWER)',
    roleMember: 'Okuma + yazma (MEMBER)',
    roleAdmin: 'Yönetici',
    createFailed: 'Belirteç oluşturulamadı.',
    columns: {
      name: 'Ad',
      token: 'Belirteç',
      role: 'Rol',
      lastUsed: 'Son kullanım',
      created: 'Oluşturulma',
    },
    snippetFilled:
      'Az önce oluşturduğunuz belirteç yukarıya yerleştirildi. Kapattıktan sonra burada görünmeyecek.',
    snippetEmpty:
      'Örneklerin gerçek bir belirtece ihtiyacı var; mevcut biri yeniden gösterilemez, yalnızca ' +
      'karması saklanıyor. “Yeni belirteçle doldur” ile bir tane üretip yerleştirin. Tam uç nokta referansı: ',
    fillWithToken: 'Yeni belirteçle doldur',
    filling: 'Oluşturuluyor…',
  },
};

export const integrations = { en, tr };
